import { useState } from "react";

import { Input } from "@/components/ui/input";
import type { Calificacion, ResultadoDelExperimento } from "@/lib/laboratorio";
import { cn } from "@/lib/utils";

const OPCIONES: { valor: Calificacion; etiqueta: string; seleccionada: string }[] = [
  { valor: "bueno", etiqueta: "Bueno", seleccionada: "bg-primary text-primary-foreground" },
  { valor: "regular", etiqueta: "Regular", seleccionada: "bg-[#e0a63a] text-white" },
  { valor: "malo", etiqueta: "Malo", seleccionada: "bg-[#c23a32] text-white" },
];

/**
 * La calificación de un texto: Bueno / Regular / Malo y un comentario opcional.
 *
 * El comentario viaja junto con la calificación (el API los guarda juntos), así
 * que no se puede escribir antes de calificar.
 */
export function CalificadorDeResultado({
  nombre,
  resultado,
  onCalificar,
}: {
  /** «del borrador», «de v2»: arma el nombre accesible. */
  nombre: string;
  resultado: ResultadoDelExperimento;
  onCalificar: (calificacion: Calificacion, comentario: string | null) => void;
}) {
  const [comentario, setComentario] = useState(resultado.comentario ?? "");
  const listo = resultado.estado === "listo";

  const comentarioActual = () => (comentario.trim() === "" ? null : comentario.trim());

  return (
    <div className="flex flex-col gap-2">
      <div
        role="radiogroup"
        aria-label={`Calificación ${nombre}`}
        className="bg-[#ececeb] inline-flex w-fit gap-0.5 rounded-[0.625rem] p-[0.1875rem]"
      >
        {OPCIONES.map((o) => {
          const activa = resultado.calificacion === o.valor;

          return (
            <button
              key={o.valor}
              type="button"
              role="radio"
              aria-checked={activa}
              disabled={!listo}
              onClick={() => onCalificar(o.valor, comentarioActual())}
              className={cn(
                "rounded-[0.4375rem] px-3 py-1 text-[0.75rem] font-bold transition-colors disabled:opacity-50",
                activa ? o.seleccionada : "text-ink-secundario hover:bg-card/70",
              )}
            >
              {o.etiqueta}
            </button>
          );
        })}
      </div>
      <Input
        aria-label={`Comentario ${nombre}`}
        placeholder="Comentario (opcional)"
        value={comentario}
        disabled={!listo || resultado.calificacion === null}
        title={resultado.calificacion === null ? "Califica primero para dejar un comentario" : undefined}
        onChange={(e) => setComentario(e.target.value)}
        onBlur={() => {
          if (resultado.calificacion !== null && comentarioActual() !== (resultado.comentario ?? null)) {
            onCalificar(resultado.calificacion, comentarioActual());
          }
        }}
        maxLength={500}
        className="h-[2.375rem] rounded-md text-[0.78125rem]"
      />
    </div>
  );
}
